import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import emailService from '../services/emailService';
import secureLogger from '../services/secureLoggingService';
import supabaseAuthService from '../services/supabaseAuthService';
import { API_ENDPOINTS } from '../config/api';
import { supabase } from '../config/supabase';

const AuthContext = createContext();

const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_AUTH_EMAIL || 'admin@gdcbeekapur.org.in';
const PROJECT_REF = 'jfghvgkvokfphzrfazsm';

/**
 * Read the Supabase auth token directly from localStorage and
 * decode the JWT.  This is synchronous and avoids the async
 * Supabase client initialization entirely — critical for
 * preventing the loading→redirect race condition.
 */
function getSessionFromStorage() {
  try {
    const key = `sb-${PROJECT_REF}-auth-token`;
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    const data = JSON.parse(raw);
    if (!data?.access_token) return null;

    // Decode the JWT payload (no signature verification —
    // this is only used to restore UI state; the Supabase
    // client will validate on the next API call).
    const parts = data.access_token.split('.');
    if (parts.length !== 3) return null;

    const payload = JSON.parse(atob(parts[1]));
    const expiresAt = payload.exp ? payload.exp * 1000 : 0;
    if (expiresAt && expiresAt < Date.now()) {
      // Token expired — clear it
      localStorage.removeItem(key);
      return null;
    }

    return {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      user: {
        id: payload.sub,
        email: payload.email,
        user_metadata: payload.user_metadata || {},
      },
    };
  } catch {
    return null;
  }
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [requiresMFA, setRequiresMFA] = useState(false);

  // Guard: set to true during username/password login to prevent
  // onAuthStateChange from clearing the user state.
  const _loginInProgress = React.useRef(false);

  // ──────────────────────────────────────────────
  // Initialize: check localStorage FIRST (sync),
  // then let Supabase recover in the background.
  // ──────────────────────────────────────────────
  useEffect(() => {
    let mounted = true;

    // 1. Synchronous fast-path: read JWT from localStorage
    const stored = getSessionFromStorage();
    if (stored?.user) {
      const email = stored.user.email;
      const admin = email?.toLowerCase() === ADMIN_EMAIL.toLowerCase();
      if (mounted) {
        setUser({
          id: stored.user.id,
          email,
          name: stored.user.user_metadata?.name || email,
          role: admin ? 'admin' : 'user',
        });
        setIsAdmin(admin);
        setLoading(false);
      }
      // Still let Supabase client pick up the session in the
      // background so API calls work.  Fire-and-forget with timeout.
      supabase.auth.getSession().catch(() => {});
    }

    // 2. Subscribe to auth state changes (for OAuth, password
    //    recovery, token refresh, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;

        // Skip during password recovery
        if (event === 'PASSWORD_RECOVERY') {
          setLoading(false);
          return;
        }

        // Skip if our own login handler is running
        if (_loginInProgress.current) return;

        if (event === 'SIGNED_IN' && session?.user) {
          const supabaseUser = session.user;
          const email = supabaseUser.email;
          const admin = email?.toLowerCase() === ADMIN_EMAIL.toLowerCase();

          setUser({
            id: supabaseUser.id,
            email,
            name: supabaseUser.user_metadata?.name || email,
            role: admin ? 'admin' : 'user',
          });
          setIsAdmin(admin);
          setLoading(false);
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setIsAdmin(false);
          setRequiresMFA(false);
          setLoading(false);
        } else if (event === 'INITIAL_SESSION' && session?.user) {
          // Session restored from storage — update state
          const supabaseUser = session.user;
          const email = supabaseUser.email;
          const admin = email?.toLowerCase() === ADMIN_EMAIL.toLowerCase();
          setUser({
            id: supabaseUser.id,
            email,
            name: supabaseUser.user_metadata?.name || email,
            role: admin ? 'admin' : 'user',
          });
          setIsAdmin(admin);
          setLoading(false);
        }
      }
    );

    // 3. If the fast-path didn't find a session, Supabase might
    //    still recover one (async).  Set a safety timeout to
    //    ensure loading always resolves.
    const safetyTimeout = setTimeout(() => {
      if (mounted) setLoading(false);
    }, 5000);

    return () => {
      mounted = false;
      clearTimeout(safetyTimeout);
      subscription?.unsubscribe?.();
    };
  }, []);

  // ──────────────────────────────────────────────
  // Admin login with username/password
  // ──────────────────────────────────────────────
  const login = async (username, password) => {
    _loginInProgress.current = true;
    try {
      const result = await supabaseAuthService.adminLoginWithPassword(username, password);

      if (result.success && result.session) {
        const sessionUser = result.user || {};
        const email = sessionUser.email || `${username}@admin.university.edu`;

        // Set user state directly — no page reload needed
        setUser({
          id: sessionUser.uid || `admin_${username}`,
          username: sessionUser.username || username,
          email,
          role: 'admin',
          name: (sessionUser.username || username).charAt(0).toUpperCase() + (sessionUser.username || username).slice(1),
        });
        setIsAdmin(true);

        // Persist session in localStorage for page reloads
        try {
          const storageKey = `sb-${PROJECT_REF}-auth-token`;
          const tokenData = {
            access_token: result.session.access_token,
            refresh_token: result.session.refresh_token,
            expires_at: result.session.expires_at || Math.floor(Date.now() / 1000) + 3600,
            expires_in: 3600,
            token_type: 'bearer',
          };
          localStorage.setItem(storageKey, JSON.stringify(tokenData));
        } catch (e) {
          console.warn('localStorage write failed:', e.message);
        }

        // Fire-and-forget: let Supabase client pick up the session
        // in the background (non-blocking, with timeout).
        const sessionPromise = supabase.auth.setSession({
          access_token: result.session.access_token,
          refresh_token: result.session.refresh_token,
        });
        Promise.race([
          sessionPromise,
          new Promise((_, rej) => setTimeout(() => rej(new Error('setSession timeout')), 8000)),
        ]).catch(() => {});

        toast.success('Login successful!');

        // Return success — caller (AdminLogin.js) will navigate
        return { success: true };
      } else {
        toast.error(result.error || 'Invalid credentials');
        return { success: false, error: result.error || 'Invalid credentials' };
      }
    } catch (error) {
      const message = error.message || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    } finally {
      // Release the guard after a small delay so onAuthStateChange
      // events that fire during setSession don't interfere
      setTimeout(() => { _loginInProgress.current = false; }, 2000);
    }
  };

  // ──────────────────────────────────────────────
  // Admin OAuth login (Google/Gmail)
  // ──────────────────────────────────────────────
  const loginWithGoogle = async () => {
    try {
      const result = await supabaseAuthService.signInWithGoogle();
      if (result.success) {
        return { success: true };
      } else {
        toast.error(result.error || 'OAuth login failed');
        return { success: false, error: result.error };
      }
    } catch (error) {
      const message = error.message || 'OAuth login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await supabaseAuthService.signOut();
      // Clear localStorage session
      try {
        localStorage.removeItem(`sb-${PROJECT_REF}-auth-token`);
      } catch {}
      setUser(null);
      setIsAdmin(false);
      setRequiresMFA(false);
      toast.success('Logged out successfully');
      return { success: true };
    } catch (error) {
      secureLogger.error('Logout error:', error);
      setUser(null);
      setIsAdmin(false);
      setRequiresMFA(false);
      return { success: false, error: error.message };
    }
  };

  const sendPasswordResetOTP = async (email) => {
    try {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        toast.error('Please enter a valid email address');
        return { success: false, error: 'Invalid email format' };
      }

      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/admin/reset-password`,
      });

      if (error) throw error;

      toast.success('Password reset link sent! Check your email (including spam folder).');
      return { success: true, message: 'Password reset link sent' };
    } catch (error) {
      secureLogger.error('Send reset email error:', error);
      const message = error.message || 'Failed to send reset link';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const resetPassword = async (token, otp, newPassword) => {
    try {
      if (!newPassword) {
        toast.error('New password is required.');
        return { success: false, error: 'Missing required fields' };
      }

      if (newPassword.length < 6) {
        toast.error('Password must be at least 6 characters');
        return { success: false, error: 'Password too short' };
      }

      const { error } = await supabase.auth.updateUser({ password: newPassword });

      if (error) throw error;

      toast.success('Password updated successfully!');
      return { success: true };
    } catch (error) {
      secureLogger.error('Reset password error:', error);
      const message = error.message || 'Password reset failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const getPasswordHistory = () => {
    return [];
  };

  const value = {
    user,
    loading,
    isAdmin,
    requiresMFA,
    login,
    loginWithGoogle,
    logout,
    sendPasswordResetOTP,
    resetPassword,
    getPasswordHistory,
    isAuthenticated: !!user,
    supabaseAuthService,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
